import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1'

export interface OrderItem {
  id: string
  product_id: string
  product_name: string
  product_sku?: string
  supplier_product_code?: string
  category?: string
  brand?: string
  unit_price: number
  quantity: number
  line_total: number
  product_specifications?: any
  product_images?: string[]
  product_description?: string
  status: string
}

export interface ShippingAddress {
  firstName: string
  lastName: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  country: string
  zipCode: string
  company?: string
}

export interface CustomerInfo {
  email: string
  phone: string
  firstName: string
  lastName: string
}

export interface Order {
  id: string
  user_id: string
  order_number: string
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded'
  subtotal: number
  tax_amount: number
  shipping_amount: number
  discount_amount: number
  total_amount: number
  currency: string
  shipping_address: ShippingAddress
  billing_address?: ShippingAddress
  customer_info: CustomerInfo
  payment_status: string
  payment_method?: string
  tracking_number?: string
  carrier?: string
  notes?: string
  admin_notes?: string
  confirmed_at?: string
  shipped_at?: string
  delivered_at?: string
  cancelled_at?: string
  created_at: string
  updated_at: string
  items?: OrderItem[]
  item_count?: number
}

export interface CreateOrderData {
  items: {
    productId: string
    quantity: number
    unitPrice: number
    lineTotal: number
  }[]
  subtotal: number
  taxAmount?: number
  shippingAmount?: number
  discountAmount?: number
  totalAmount: number
  currency?: string
  shippingAddress: ShippingAddress
  billingAddress?: ShippingAddress
  customerInfo: CustomerInfo
  paymentMethod?: string
  notes?: string
}

export interface OrdersResponse {
  orders: Order[]
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

export interface OrderFilters {
  page?: number
  limit?: number
  status?: string
  search?: string
}

class OrdersService {
  private baseURL: string

  constructor() {
    this.baseURL = `${API_URL}/orders`
  }

  private headers() {
    const token = localStorage.getItem('access_token')
    return { Authorization: `Bearer ${token}` }
  }

  // Get user's orders (client view)
  async getMyOrders(filters: OrderFilters = {}): Promise<OrdersResponse> {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.status) params.append('status', filters.status)
    if (filters.search) params.append('search', filters.search)

    const res = await axios.get(`${this.baseURL}?${params.toString()}`, { 
      headers: this.headers() 
    })
    return res.data.data
  }

  // Get all orders (admin view)
  async getAllOrders(filters: OrderFilters = {}): Promise<OrdersResponse> {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.status) params.append('status', filters.status)
    if (filters.search) params.append('search', filters.search)

    const res = await axios.get(`${this.baseURL}/admin?${params.toString()}`, { 
      headers: this.headers() 
    })
    return res.data.data
  }

  // Get specific order
  async getOrder(id: string): Promise<Order> {
    const res = await axios.get(`${this.baseURL}/${id}`, { 
      headers: this.headers() 
    })
    return res.data.data.order
  }

  // Create new order
  async createOrder(orderData: CreateOrderData): Promise<Order> {
    const res = await axios.post(this.baseURL, orderData, { 
      headers: { ...this.headers(), 'Content-Type': 'application/json' }
    })
    return res.data.data.order
  }

  // Update order status (admin only)
  async updateOrderStatus(id: string, data: {
    status: Order['status']
    trackingNumber?: string
    carrier?: string
    adminNotes?: string
  }): Promise<void> {
    await axios.put(`${this.baseURL}/${id}/status`, data, { 
      headers: { ...this.headers(), 'Content-Type': 'application/json' }
    })
  }

  // Cancel order (client can cancel pending orders)
  async cancelOrder(id: string, reason?: string): Promise<void> {
    await axios.put(`${this.baseURL}/${id}/cancel`, { reason }, { 
      headers: { ...this.headers(), 'Content-Type': 'application/json' }
    })
  }

  // Get order statistics (admin only)
  async getOrderStats(): Promise<{
    totalOrders: number
    pendingOrders: number
    shippedOrders: number
    deliveredOrders: number
    totalRevenue: number
    monthlyRevenue: number
    topProducts: Array<{
      product_name: string
      total_quantity: number
      total_revenue: number
    }>
  }> {
    const res = await axios.get(`${this.baseURL}/stats`, { 
      headers: this.headers() 
    })
    return res.data.data
  }
}

export const ordersService = new OrdersService()
export default ordersService
